import React, { useState, useEffect } from 'react'
import { createCustomer, getStatements } from '@lib/spektrix/customer'
import { validateEmail, validatePhone, validatePassword, validatePasswordMatch, validateRequired } from '@lib/validation'
import PasswordInput from '../shared/password-input'

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  })

  const [statements, setStatements] = useState([])
  const [agreedStatements, setAgreedStatements] = useState([])
  const [termsAccepted, setTermsAccepted] = useState(false)

  const [isLoadingStatements, setIsLoadingStatements] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    loadStatements()
  }, [])

  const loadStatements = async () => {
    setIsLoadingStatements(true)
    try {
      const statementsData = await getStatements()
      setStatements(statementsData || [])
    } catch (error) {
      console.error('Failed to load statements:', error)
      // Continue even if statements fail to load
      setStatements([])
    } finally {
      setIsLoadingStatements(false)
    }
  }

  const handleInputChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value
    })
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: null })
    }
  }

  const handleStatementToggle = (statementId) => {
    if (agreedStatements.includes(statementId)) {
      setAgreedStatements(agreedStatements.filter(id => id !== statementId))
    } else {
      setAgreedStatements([...agreedStatements, statementId])
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Required fields
    if (!validateRequired(formData.firstName)) {
      newErrors.firstName = 'First name is required'
    }

    if (!validateRequired(formData.lastName)) {
      newErrors.lastName = 'Last name is required'
    }

    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email address is required'
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // Phone is optional, but validate if provided
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number'
    }

    // Password validation
    const passwordValidation = validatePassword(formData.password)
    if (!passwordValidation.valid) {
      newErrors.password = passwordValidation.errors[0]
    }

    // Password match
    if (!validatePasswordMatch(formData.password, formData.confirmPassword)) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    // Terms and conditions
    if (!termsAccepted) {
      newErrors.terms = 'You must accept the terms and conditions'
    }

    return newErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    // Validate form
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setIsSubmitting(true)

    try {
      const domain = window.location.hostname

      // Prepare customer data
      const customerData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password
      }

      // Add phone if provided
      if (formData.phone.trim()) {
        customerData.phone = formData.phone.trim()
      }

      // Add agreed statements as array of strings (as per Spektrix API spec)
      if (agreedStatements.length > 0) {
        customerData.agreedStatements = agreedStatements.map(id => String(id))
      }

      await createCustomer(customerData, domain)

      setSuccess(true)
    } catch (error) {
      console.error('Registration failed:', error)
      setErrors({
        submit: error.message || 'Failed to create account. This email may already be registered.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Success state
  if (success) {
    return (
      <div className="space-y-6">
        <div className="bg-green-50 dark:bg-green-900 dark:bg-opacity-20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400 dark:text-green-500"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                Account created successfully!
              </h3>
              <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                <p>
                  Your account has been created. You can now sign in with your email and password.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <a
            href="/my-account"
            className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md transition-colors"
          >
            Sign In
          </a>
        </div>
      </div>
    )
  }

  return (
    <div>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Create your account to book tickets, manage your orders, and save your preferences.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.submit && (
          <div className="bg-red-50 dark:bg-red-900 dark:bg-opacity-20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
          </div>
        )}

        {/* Personal Information */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Personal Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                First Name
                <span className="text-red-500 dark:text-red-400 ml-1">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                className={`w-full px-3 py-2 border ${
                  errors.firstName
                    ? 'border-red-500 dark:border-red-400'
                    : 'border-gray-300 dark:border-gray-600'
                } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
                required
                disabled={isSubmitting}
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Last Name
                <span className="text-red-500 dark:text-red-400 ml-1">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                className={`w-full px-3 py-2 border ${
                  errors.lastName
                    ? 'border-red-500 dark:border-red-400'
                    : 'border-gray-300 dark:border-gray-600'
                } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
                required
                disabled={isSubmitting}
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Email Address
              <span className="text-red-500 dark:text-red-400 ml-1">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              className={`w-full px-3 py-2 border ${
                errors.email
                  ? 'border-red-500 dark:border-red-400'
                  : 'border-gray-300 dark:border-gray-600'
              } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
              placeholder="you@example.com"
              required
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={handleInputChange('phone')}
              className={`w-full px-3 py-2 border ${
                errors.phone
                  ? 'border-red-500 dark:border-red-400'
                  : 'border-gray-300 dark:border-gray-600'
              } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
              placeholder="01234 567890"
              disabled={isSubmitting}
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.phone}</p>
            )}
          </div>
        </div>

        {/* Password */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Password
          </h2>

          <PasswordInput
            id="password"
            label="Password"
            value={formData.password}
            onChange={handleInputChange('password')}
            placeholder="Create a password"
            showStrengthIndicator={true}
            required={true}
            disabled={isSubmitting}
            error={errors.password}
          />

          <PasswordInput
            id="confirmPassword"
            label="Confirm Password"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            placeholder="Confirm your password"
            required={true}
            disabled={isSubmitting}
            error={errors.confirmPassword}
          />
        </div>

        {/* Communication Preferences */}
        {!isLoadingStatements && statements.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Communication Preferences
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Choose how you'd like to hear from us
            </p>

            <div className="space-y-3">
              {statements.map((statement) => (
                <label
                  key={statement.id}
                  className="flex items-start p-3 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={agreedStatements.includes(statement.id)}
                    onChange={() => handleStatementToggle(statement.id)}
                    className="mt-1 h-4 w-4 text-blue-600 dark:text-blue-500 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400"
                    disabled={isSubmitting}
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {statement.text || statement.name}
                    </span>
                    {statement.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {statement.description}
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Terms and Conditions */}
        <div>
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked)
                if (errors.terms) {
                  setErrors({ ...errors, terms: null })
                }
              }}
              className="mt-1 h-4 w-4 text-blue-600 dark:text-blue-500 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400"
              disabled={isSubmitting}
            />
            <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
              I accept the{' '}
              <a
                href="/terms-and-conditions"
                target="_blank"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                terms and conditions
              </a>{' '}
              and{' '}
              <a
                href="/privacy"
                target="_blank"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                privacy policy
              </a>
              <span className="text-red-500 dark:text-red-400 ml-1">*</span>
            </span>
          </label>
          {errors.terms && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.terms}</p>
          )}
        </div>

        <button
          type="submit"
          className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Creating Account...
            </span>
          ) : (
            'Create Account'
          )}
        </button>

        <div className="text-center pt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <a
              href="/my-account"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              Sign in here
            </a>
          </p>
        </div>
      </form>
    </div>
  )
}

export default Register
